import { useEffect, useState } from "react";
import { PRICE_COPY, PRICE_DETAIL, isEntitled } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { buy, canBuy, restore, storeProduct } from "../store";

/**
 * The subscription, on the tablet, behind the grown-up's password.
 *
 * That password is the parental gate the Kids Category requires before a
 * purchase, and it is a stronger one than the arithmetic puzzle most apps use —
 * it was set by the adult from an emailed link, on a device the child was not
 * holding.
 *
 * Everything Apple insists a buyer can see before they buy is on this screen
 * rather than behind a link: the price, the period, what it covers, and that it
 * renews until cancelled. Not only because review checks for it — a parent
 * deciding in ten seconds should not have to open anything to find the price.
 */
export function Paywall({ onChanged }: { onChanged: () => void }) {
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState("");
  const [until, setUntil] = useState<Date | null>(null);
  const [accountToken, setAccountToken] = useState<string | undefined>();
  // Apple's own localised price beats ours: it is right in every storefront and
  // ours is right in one.
  const [price, setPrice] = useState(PRICE_COPY);
  const [busy, setBusy] = useState<"buy" | "restore" | null>(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const s = await api.subscription();
      setSummary(s.summary);
      setStatus(s.status);
      setUntil(s.entitledUntil ? new Date(s.entitledUntil) : null);
      setAccountToken(s.accountToken);
    } catch {
      /* the screen still works without it; the buttons are what matter */
    }
    const p = await storeProduct();
    if (p.available && p.price) setPrice(p.price);
  };

  useEffect(() => {
    void load();
  }, []);

  /**
   * Hands the signed transaction to the server and shows what it decided.
   *
   * The device reports; it never grants. Everything about how long the
   * subscription lasts comes back from what Apple signed, checked server-side.
   */
  const report = async (jws: string) => {
    try {
      await api.reportPurchase(jws);
      setNote("Thank you — Howcomet is awake.");
      await load();
      onChanged();
    } catch (e) {
      setNote(
        e instanceof ApiError ? e.message : "We couldn't confirm that with Apple. Try again.",
      );
    }
  };

  const start = async () => {
    if (busy) return;
    setBusy("buy");
    setNote("");
    try {
      const result = await buy(accountToken);
      if (result.status === "purchased") await report(result.jws);
      else if (result.status === "pending")
        // Ask to Buy. Common in exactly the family setup this app is for, and
        // saying nothing here looks like the button did not work.
        setNote("Asked. Someone else on your Apple Account needs to approve it.");
      else if (result.status === "unavailable")
        setNote("Subscribing only works in the Howcomet app on your iPad.");
    } catch (e) {
      setNote(e instanceof Error ? e.message : "That didn't work.");
    } finally {
      setBusy(null);
    }
  };

  const restoreExisting = async () => {
    if (busy) return;
    setBusy("restore");
    setNote("");
    try {
      const result = await restore();
      if (result.status === "restored") await report(result.jws);
      else if (result.status === "nothing") setNote("No subscription found on this Apple Account.");
      else if (result.status === "unavailable")
        setNote("Restoring only works in the Howcomet app on your iPad.");
    } finally {
      setBusy(null);
    }
  };

  /*
    Whether they still have access — the same question the server's gate asks,
    answered by the same function, from the date the route already sends.

    It used to read `status === "active" || status === "trialing"`, which is the
    status without the date, and that hid the Subscribe button from exactly the
    people who wanted it: a family whose trial had run out still had the status
    `trialing`, so the only button on the screen was Restore. A paywall that
    cannot be paid is worse than no paywall.
  */
  const active = isEntitled({ entitledUntil: until, planStatus: status });

  /*
    Whether to OFFER the purchase, which is a different question from whether
    they have access, and the trial is exactly where the two come apart.

    Gating the button on `active` alone was the same mistake as the one above,
    one step further along: a family on day two of a working trial is entitled,
    so the price and the Subscribe button vanished and the only control left was
    Restore — which correctly reports finding nothing, because they have never
    bought anything. A parent who has decided early is the parent you most want
    to be able to say yes, and they were told to come back when their child had
    been locked out.

    A trial is access without a payment. So the question this asks is "are they
    already paying?", which hides the button for `apple/active` and `apple/grace`
    — real subscribers, who must not be sold a second one — and for a comped
    account, while leaving it up for the whole trial and after it lapses.

    App Review depends on this too: 2.1(b) wants the purchase "visible to the
    reviewer and functional", and every entitled state used to hide it.
  */
  const paying = active && status !== "trialing";

  return (
    <div className="mt-5 rounded-3xl bg-panel p-5">
      <p className="disp text-[20px] text-cream">Subscription</p>
      {summary && <p className="mt-1 text-[15px] leading-relaxed text-cream/70">{summary}</p>}

      {!paying && (
        <>
          <p className="disp mt-4 text-[26px] text-gold">{price}</p>
          <p className="mt-1 text-[15px] leading-relaxed text-cream/70">{PRICE_DETAIL}</p>
          <p className="mt-2 text-[13px] leading-relaxed text-cream/45">
            Renews every month until you cancel. Cancel any time in your Apple Account settings.
          </p>
        </>
      )}

      {note && <p className="mt-3 text-[15px] font-bold text-amber">{note}</p>}

      {!paying && (
        <button
          onClick={() => void start()}
          disabled={busy !== null}
          className="kidbtn disp mt-4 w-full rounded-3xl bg-gold py-4 text-[19px] text-night disabled:bg-cream/15 disabled:text-cream/40"
        >
          {busy === "buy" ? "Asking Apple…" : `Subscribe — ${price}`}
        </button>
      )}

      {/* Apple requires a way to restore a purchase, and a family that reinstalls
          or moves to a second tablet needs it whether Apple asked or not. */}
      <button
        onClick={() => void restoreExisting()}
        disabled={busy !== null}
        className="kidbtn mt-3 w-full rounded-2xl bg-cream/10 py-3 text-[15px] font-bold text-cream/80"
      >
        {busy === "restore" ? "Checking…" : "Restore a subscription"}
      </button>

      {!canBuy() && (
        <p className="mt-3 text-[13px] leading-relaxed text-cream/45">
          Buying happens in the Howcomet app on the iPad — this page can only show you where
          things stand.
        </p>
      )}

      <p className="mt-4 text-[13px] text-cream/40">
        <a className="underline" href="https://howcomet.com/terms" target="_blank" rel="noopener">
          Terms
        </a>{" "}
        ·{" "}
        <a className="underline" href="https://howcomet.com/privacy" target="_blank" rel="noopener">
          Privacy
        </a>
      </p>
    </div>
  );
}
